const parser = new DOMParser();

// Simple and fast parsing function
function parseXMLNodes(
  nodes: HTMLCollectionOf<Element>,
  idTag: string[],
  titleTag: string[]
): { [id: string]: string } {
  const products: { [id: string]: string } = {};
  const nodeArray = Array.from(nodes);

  // Process in larger batches
  const batchSize = 5000;

  for (let i = 0; i < nodeArray.length; i += batchSize) {
    const batch = nodeArray.slice(i, Math.min(i + batchSize, nodeArray.length));

    for (const item of batch) {
      let id: string | null = null;
      let title: string | null = null;

      // Try each possible tag for id
      for (const tag of idTag) {
        const element = item.getElementsByTagName(tag)[0];
        if (element?.textContent) {
          id = element.textContent;
          break;
        }
      }

      // Try each possible tag for title
      for (const tag of titleTag) {
        const element = item.getElementsByTagName(tag)[0];
        if (element?.textContent) {
          title = element.textContent;
          break;
        }
      }

      if (id && title) {
        products[id] = title;
      }
    }

    // Allow other operations to process between batches
    if (i + batchSize < nodeArray.length) {
      setTimeout(() => {}, 0);
    }
  }

  return products;
}

// Optimized Google Shopping parser
export function parseGoogleFeed(feedContent: string): { [id: string]: string } {
  const xmlDoc = parser.parseFromString(feedContent, "application/xml");
  const items = xmlDoc.getElementsByTagName("item");
  return parseXMLNodes(items, ["g:id", "id"], ["title", "g:title"]);
}

// Optimized PEMACZ parser
export function parsePemaczFeed(feedContent: string): { [id: string]: string } {
  const xmlDoc = parser.parseFromString(feedContent, "application/xml");
  const items = xmlDoc.getElementsByTagName("entry");
  return parseXMLNodes(items, ["g:id", "id"], ["g:title", "title"]);
}

// Optimized Heureka parser
export function parseHeurekaFeed(feedContent: string): {
  [id: string]: string;
} {
  const xmlDoc = parser.parseFromString(feedContent, "application/xml");
  const items = xmlDoc.getElementsByTagName("SHOPITEM");
  return parseXMLNodes(items, ["ITEM_ID"], ["PRODUCTNAME"]);
}

// Fast feed type detection using regex first
export function detectFeedType(feedContent: string): string {
  // Quick check using regex before parsing XML
  if (feedContent.includes("<item>")) return "google";
  if (feedContent.includes("<entry>")) return "pemacz";
  if (feedContent.includes("<SHOPITEM>")) return "heureka";

  // Fallback to XML parsing if needed
  const sampleContent = feedContent.slice(0, 1000);
  const xmlDoc = parser.parseFromString(sampleContent, "application/xml");

  if (xmlDoc.getElementsByTagName("item").length > 0) return "google";
  if (
    xmlDoc.getElementsByTagName("entry").length > 0 ||
    xmlDoc.getElementsByTagName("atom:entry").length > 0
  )
    return "pemacz";
  if (xmlDoc.getElementsByTagName("SHOPITEM").length > 0) return "heureka";

  throw new Error("Neznámý typ feedu");
}

// Optimized main parser function with type checking
export function parseFeed(feedContent: string): { [id: string]: string } {
  const feedType = detectFeedType(feedContent);

  const parserMap: {
    [key: string]: (content: string) => { [id: string]: string };
  } = {
    google: parseGoogleFeed,
    pemacz: parsePemaczFeed,
    heureka: parseHeurekaFeed,
  };

  const parser = parserMap[feedType];
  if (!parser) {
    throw new Error(`Unsupported feed type: ${feedType}`);
  }

  return parser(feedContent);
}

export function extractShopLink(feedContent: string): string {
  try {
    // First try to find URL using regex
    const urlMatch = feedContent.match(
      /<(g:link|link|URL)>\s*(https?:\/\/[^\s<]+)\s*<\/(g:link|link|URL)>/i
    );

    if (urlMatch && urlMatch[2]) {
      const domainMatch = urlMatch[2].match(
        /^(?:https?:\/\/)?(?:www\.)?([^/]+)/
      );
      return domainMatch?.[1] ?? "link missing";
    }

    // Fallback to XML parsing if regex fails
    const sampleContent = feedContent.slice(0, 5000);
    const xmlDoc = parser.parseFromString(sampleContent, "application/xml");
    const feedType = detectFeedType(sampleContent);

    const itemElementMap: { [key: string]: string } = {
      google: "item",
      pemacz: "entry",
      heureka: "SHOPITEM",
    };

    const itemElement = itemElementMap[feedType];
    const items = xmlDoc.getElementsByTagName(itemElement);

    if (items.length === 0) return "link missing";

    const firstItem = items[0];
    const linkElement =
      feedType === "heureka"
        ? firstItem.getElementsByTagName("URL")[0]
        : firstItem.getElementsByTagName("g:link")[0] ||
          firstItem.getElementsByTagName("link")[0];

    if (!linkElement?.textContent) return "link missing";

    const domainMatch = linkElement.textContent.match(
      /^(?:https?:\/\/)?(?:www\.)?([^/]+)/
    );
    return domainMatch?.[1] ?? "link missing";
  } catch (error) {
    console.error("Error extracting shop link:", error);
    return "link missing";
  }
}
